/**
 * Image preloader utility for concurrent image loading during PDF generation
 * This optimizes PDF generation by loading all images in parallel before rendering
 */

/**
 * Preloads a single image with timeout and error handling
 * @param {string} src - Image source URL
 * @param {number} timeout - Timeout in milliseconds (default: 10000)
 * @returns {Promise<{success: boolean, image?: HTMLImageElement, error?: Error, src: string}>}
 */
async function preloadSingleImage(src, timeout = 10000) {
  const cleanSrc = (src || '').trim();
  const tryHtmlImage = () => new Promise((resolve) => {
    const img = new Image();
    const timeoutId = setTimeout(() => {
      console.warn(`Image preload timeout: ${cleanSrc}`);
      resolve({ success: false, error: new Error('Image load timeout'), src: cleanSrc });
    }, timeout);

    img.onload = () => {
      clearTimeout(timeoutId);
      console.log(`Image preloaded successfully: ${cleanSrc}`);
      resolve({ success: true, image: img, src: cleanSrc });
    };
    
    img.onerror = (error) => {
      clearTimeout(timeoutId);
      console.warn(`Image preload failed: ${cleanSrc}`, error);
      resolve({ success: false, error: new Error(`Image load failed: ${cleanSrc}`), src: cleanSrc });
    };
    
    img.src = cleanSrc;
  });

  const fetchToDataUrl = async (url, timeoutMs = 12000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    const toDataUrl = async (blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const tryFetch = async (u) => {
      const res = await fetch(u, { signal: controller.signal, mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await toDataUrl(await res.blob());
    };
    try {
      return await tryFetch(cleanSrc);
    } catch (e1) {
      try {
        const u = new URL(cleanSrc);
        const noProto = `${u.host}${u.pathname}${u.search}`;
        const proxied = `https://images.weserv.nl/?url=${encodeURIComponent(noProto)}&w=1600&output=jpg`;
        return await tryFetch(proxied);
      } finally {
        clearTimeout(id);
      }
    }
  };

  // First try normal image load
  const first = await tryHtmlImage();
  if (first.success) return first;

  // If that fails, try to fetch as data URL and create an Image from it
  try {
    const dataUrl = await fetchToDataUrl(cleanSrc, Math.max(6000, timeout));
    const fromDataUrl = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ success: true, image: img, src: cleanSrc });
      img.onerror = () => resolve({ success: false, error: new Error('dataURL load failed'), src: cleanSrc });
      img.src = dataUrl;
    });
    if (fromDataUrl.success) {
      console.log(`Image preloaded via dataURL: ${cleanSrc}`);
    }
    return fromDataUrl;
  } catch (e) {
    console.warn('preloadSingleImage dataURL fallback failed:', e);
    return first; // return the first failure
  }
}

/**
 * Flattens image transparency for Lulu compatibility
 * @param {HTMLImageElement} img - The loaded image
 * @param {string} src - Original image source
 * @returns {Promise<HTMLImageElement>}
 */
async function flattenImageTransparency(img, src) {
  return new Promise((resolve) => {
    try {
      // Create a canvas to flatten the image
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw with white background to eliminate transparency
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      // Convert to JPEG (no transparency) with high quality
      const flattenedDataUrl = canvas.toDataURL('image/jpeg', 0.95);
      
      // Create new image with flattened data
      const flattenedImg = new Image();
      flattenedImg.onload = () => {
        console.log(`Image transparency flattened: ${src}`);
        resolve(flattenedImg);
      };
      flattenedImg.onerror = () => {
        console.warn(`Failed to flatten image, using original: ${src}`);
        resolve(img);
      };
      flattenedImg.src = flattenedDataUrl;
    } catch (error) {
      console.error('Error flattening image transparency:', error);
      resolve(img);
    }
  });
}

/**
 * Extracts all image sources from elements across multiple spreads
 * @param {Array} elements - Array of elements to scan for images
 * @returns {Array<string>} - Array of unique image source URLs
 */
function extractImageSources(elements) {
  const imageSources = new Set();
  
  elements.forEach(element => {
    if (element?.type === 'image' && element.src) {
      imageSources.add((element.src || '').trim());
    }
  });
  
  return Array.from(imageSources);
}

/**
 * Preloads all images concurrently from a list of elements
 * @param {Array} elements - Array of elements containing images
 * @param {Object} options - Options for preloading
 * @param {boolean} [options.flattenTransparency] - Whether to flatten transparency for Lulu compatibility
 * @param {number} [options.timeout] - Timeout per image in milliseconds
 * @param {function} [options.onProgress] - Progress callback (loaded, total)
 * @param {number} [options.concurrency] - Max number of simultaneous image loads
 * @param {number} [options.overallTimeout] - Max time to spend preloading before proceeding
 * @returns {Promise<Map<string, {success: boolean, image?: HTMLImageElement, error?: Error}>>}
 */
export async function preloadImages(elements, options = {}) {
  const {
    flattenTransparency = false,
    timeout = 10000,
    onProgress = null,
    concurrency = 3,
    overallTimeout = 8000
  } = options;

  // Extract unique image sources
  const imageSources = extractImageSources(elements);
  
  if (imageSources.length === 0) {
    console.log('No images to preload');
    return new Map();
  }

  console.log(`Starting preload of ${imageSources.length} images with concurrency=${concurrency} and overallTimeout=${overallTimeout}ms...`);

  // Progress and results containers
  let loadedCount = 0;
  const results = new Map();

  // Worker function to process one src
  const processOne = async (src) => {
    const result = await preloadSingleImage(src, timeout);
    if (result.success && flattenTransparency && result.image) {
      const s = src.toLowerCase();
      const shouldFlatten = !s.endsWith('.jpg') && !s.endsWith('.jpeg') && !s.startsWith('data:image/jpeg');
      if (shouldFlatten) {
        try {
          const flattenedImage = await flattenImageTransparency(result.image, src);
          result.image = flattenedImage;
        } catch (error) {
          console.warn(`Failed to flatten transparency for ${src}:`, error);
        }
      }
    }
    results.set(src, result);
    loadedCount++;
    if (onProgress) onProgress(loadedCount, imageSources.length);
  };

  // Concurrency-limited pool
  let index = 0;
  const workers = new Array(Math.min(concurrency, imageSources.length)).fill(0).map(async () => {
    while (index < imageSources.length) {
      const current = imageSources[index++];
      try {
        // Yield between tasks to keep UI responsive
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 0));
        // eslint-disable-next-line no-await-in-loop
        await processOne(current);
      } catch (e) {
        console.warn('Image processing error:', e);
      }
    }
  });

  // Apply overall timeout: return whatever is loaded by the deadline
  await Promise.race([
    Promise.allSettled(workers),
    new Promise((resolve) => setTimeout(resolve, overallTimeout))
  ]);

  const successCount = Array.from(results.values()).filter(r => r.success).length;
  const failureCount = results.size - successCount;
  
  console.log(`Image preloading completed: ${successCount} successful, ${failureCount} failed`);
  
  return results;
}

/**
 * Gets a preloaded image from the results map
 * @param {Map} preloadedImages - Results from preloadImages()
 * @param {string} src - Image source URL
 * @returns {HTMLImageElement|null} - Preloaded image or null if not found/failed
 */
export function getPreloadedImage(preloadedImages, src) {
  const key = (src || '').trim();
  const result = preloadedImages.get(key);
  return (result && result.success) ? result.image : null;
}

/**
 * Checks if an image was successfully preloaded
 * @param {Map} preloadedImages - Results from preloadImages()
 * @param {string} src - Image source URL
 * @returns {boolean} - True if image was successfully preloaded
 */
export function isImagePreloaded(preloadedImages, src) {
  const key = (src || '').trim();
  const result = preloadedImages.get(key);
  return result && result.success;
}
